import { Card, GameMode, GameState, Player, StakesLevel, Trick, PlayedCard, BotProfile } from './types';
import { createDeck, shuffleDeck } from './deck';

export const TARGET_SCORE = 16;

export function createGame(
  mode: GameMode,
  playerConfigs: Array<{ id: string; name: string; isBot: boolean; botProfile?: BotProfile; team?: 'A' | 'B' }>
): GameState {
  const expectedCount = mode === 'duel' ? 2 : 4;
  if (playerConfigs.length !== expectedCount) {
    throw new Error(`Mode "${mode}" requires exactly ${expectedCount} players, got ${playerConfigs.length}`);
  }

  const players: Player[] = playerConfigs.map((cfg, index) => {
    let team = cfg.team;
    if (mode === 'teams' && !team) {
      team = index % 2 === 0 ? 'A' : 'B';
    }
    return {
      id: cfg.id,
      name: cfg.name,
      isBot: cfg.isBot,
      botProfile: cfg.botProfile,
      team,
      score: 0,
      hand: [],
      folded: false,
      connected: true,
    };
  });

  const state: GameState = {
    mode,
    status: 'waiting',
    roundNumber: 0,
    dealerIndex: -1,
    currentTurnPlayerId: '',
    players,
    deckRemaining: 30,
    currentTrick: {
      trickNumber: 1,
      leaderId: '',
      plays: [],
      winnerId: null,
      isTied: false,
      corvenSlayWinnerId: null,
    },
    completedTricks: [],
    challenge: {
      level: 'normal',
      callerId: null,
      status: 'none',
      responses: {},
    },
    carriedOverBonus: 1,
    roundWinnerId: null,
    matchWinnerId: null,
    logs: [],
  };

  return startRound(state);
}

export function startRound(state: GameState, customDeck?: Card[]): GameState {
  const nextRoundNumber = state.roundNumber + 1;
  const nextDealerIndex = state.dealerIndex === -1 ? 0 : (state.dealerIndex + 1) % state.players.length;
  const firstTurnPlayerIndex = (nextDealerIndex + 1) % state.players.length;
  const firstPlayerId = state.players[firstTurnPlayerIndex].id;

  const deck = customDeck ? [...customDeck] : shuffleDeck(createDeck());

  // Deal 3 cards to each player
  const updatedPlayers = state.players.map((p) => {
    const hand = deck.splice(0, 3);
    return {
      ...p,
      hand,
      folded: false,
    };
  });

  const initialTrick: Trick = {
    trickNumber: 1,
    leaderId: firstPlayerId,
    plays: [],
    winnerId: null,
    isTied: false,
    corvenSlayWinnerId: null,
  };

  return {
    ...state,
    status: 'playing',
    roundNumber: nextRoundNumber,
    dealerIndex: nextDealerIndex,
    currentTurnPlayerId: firstPlayerId,
    players: updatedPlayers,
    deckRemaining: deck.length,
    currentTrick: initialTrick,
    completedTricks: [],
    challenge: {
      level: 'normal',
      callerId: null,
      status: 'none',
      responses: {},
    },
    roundWinnerId: null,
    matchWinnerId: null,
    logs: [
      ...state.logs,
      {
        id: `log_${Date.now()}_${Math.random()}`,
        timestamp: Date.now(),
        messageKey: 'round_num',
        params: { num: nextRoundNumber },
      },
    ],
  };
}

export function getActivePlayers(state: GameState): Player[] {
  return state.players.filter((p) => !p.folded);
}

export function canCallBitIt(state: GameState, playerId: string): boolean {
  if (state.status !== 'playing') return false;

  const player = state.players.find((p) => p.id === playerId);
  if (!player || player.folded) return false;

  // Case 1: A challenge is currently pending response
  if (state.challenge.status === 'pending') {
    // A player can only respond with a re-raise (double_bit_it) if:
    // 1. Current challenge level is 'bit_it' (cannot raise past double_bit_it)
    // 2. The player is one of the challenged opponents with a pending response
    return (
      state.challenge.level === 'bit_it' &&
      state.challenge.responses[playerId] === 'pending'
    );
  }

  // Case 2: No challenge is pending
  if (state.challenge.level === 'normal') {
    return true; // Any active player can initiate bit_it
  } else if (state.challenge.level === 'bit_it') {
    // Only the opposing side/player can escalate to double_bit_it
    if (state.mode === 'duel' || state.mode === 'teams') {
      const caller = state.players.find((p) => p.id === state.challenge.callerId);
      if (!caller) return true;
      if (state.mode === 'teams') {
        return player.team !== caller.team;
      }
      return player.id !== caller.id;
    }
    // In FFA, any remaining player other than the caller can escalate
    return player.id !== state.challenge.callerId;
  }
  // double_bit_it is the max cap
  return false;
}

export function callBitIt(state: GameState, playerId: string): GameState {
  if (!canCallBitIt(state, playerId)) {
    throw new Error(`Player "${playerId}" cannot call Bit-It in current state`);
  }

  const nextLevel: StakesLevel = state.challenge.level === 'normal' ? 'bit_it' : 'double_bit_it';
  const player = state.players.find((p) => p.id === playerId)!;

  // Find opponents who must respond
  const responses: Record<string, 'pending' | 'face' | 'fold'> = {};
  for (const p of state.players) {
    if (p.folded || p.id === playerId) continue;
    if (state.mode === 'teams' && p.team === player.team) continue;
    responses[p.id] = 'pending';
  }

  const stakesPoints = getStakesWinPoints(nextLevel, state.carriedOverBonus);
  const logKey = nextLevel === 'bit_it' ? 'event_bit_it_called' : 'event_double_bit_it_called';

  return {
    ...state,
    challenge: {
      level: nextLevel,
      callerId: playerId,
      status: 'pending',
      responses,
    },
    logs: [
      ...state.logs,
      {
        id: `log_${Date.now()}_${Math.random()}`,
        timestamp: Date.now(),
        messageKey: logKey,
        params: { name: player.name, stakes: stakesPoints },
      },
    ],
  };
}

export function respondChallenge(
  state: GameState,
  playerId: string,
  response: 'face' | 'fold'
): GameState {
  if (state.challenge.status !== 'pending') {
    throw new Error('No pending challenge to respond to');
  }
  if (!state.challenge.responses[playerId] || state.challenge.responses[playerId] !== 'pending') {
    throw new Error(`Player "${playerId}" has no pending response`);
  }

  const player = state.players.find((p) => p.id === playerId)!;
  const updatedResponses = {
    ...state.challenge.responses,
    [playerId]: response,
  };

  const logKey = response === 'face' ? 'event_player_faced' : 'event_player_folded';
  let newState: GameState = {
    ...state,
    challenge: {
      ...state.challenge,
      responses: updatedResponses,
    },
    logs: [
      ...state.logs,
      {
        id: `log_${Date.now()}_${Math.random()}`,
        timestamp: Date.now(),
        messageKey: logKey,
        params: { name: player.name },
      },
    ],
  };

  if (response === 'fold') {
    if (state.mode === 'duel' || state.mode === 'teams') {
      // In 1v1 or 2v2, if the opponent folds, caller immediately wins the round!
      return awardBluffWin(newState, state.challenge.callerId!);
    } else {
      // FFA mode: folding player marks folded = true, discards hand
      newState = {
        ...newState,
        players: newState.players.map((p) => (p.id === playerId ? { ...p, folded: true, hand: [] } : p)),
      };

      // Check if ALL opponents folded
      const allFolded = Object.values(updatedResponses).every((r) => r === 'fold');
      if (allFolded) {
        return awardBluffWin(newState, state.challenge.callerId!);
      }
    }
  }

  // Check if all pending responses are resolved
  const anyPending = Object.values(updatedResponses).some((r) => r === 'pending');
  if (!anyPending) {
    let nextTurnPlayerId = newState.currentTurnPlayerId;
    const currentTurnPlayer = newState.players.find((p) => p.id === nextTurnPlayerId);
    if (!currentTurnPlayer || currentTurnPlayer.folded) {
      nextTurnPlayerId = getNextActivePlayerId(newState.players, nextTurnPlayerId);
    }

    newState = {
      ...newState,
      currentTurnPlayerId: nextTurnPlayerId,
      challenge: {
        ...newState.challenge,
        status: 'resolved',
      },
    };
  }

  return newState;
}

function awardBluffWin(state: GameState, winnerId: string): GameState {
  const winner = state.players.find((p) => p.id === winnerId)!;
  const isPending = state.challenge.status === 'pending';
  const foldPoints = getStakesFoldPoints(state.challenge.level, state.carriedOverBonus, isPending);

  const updatedPlayers = state.players.map((p) => {
    const isWinner = state.mode === 'teams' ? p.team === winner.team : p.id === winnerId;
    return isWinner ? { ...p, score: p.score + foldPoints } : p;
  });

  const matchWinner = updatedPlayers.find((p) => p.score >= TARGET_SCORE);

  return {
    ...state,
    status: matchWinner ? 'match_end' : 'round_end',
    players: updatedPlayers,
    carriedOverBonus: 1,
    roundWinnerId: winnerId,
    matchWinnerId: matchWinner ? (state.mode === 'teams' ? winner.team! : matchWinner.id) : null,
    logs: [
      ...state.logs,
      {
        id: `log_${Date.now()}_${Math.random()}`,
        timestamp: Date.now(),
        messageKey: 'event_bluff_win',
        params: { name: winner.name },
      },
      {
        id: `log_${Date.now()}_${Math.random()}_win`,
        timestamp: Date.now(),
        messageKey: matchWinner ? 'event_match_won' : 'event_round_won',
        params: { name: winner.name, points: matchWinner ? winner.score + foldPoints : foldPoints },
      },
    ],
  };
}

export function surrenderRound(state: GameState, playerId: string): GameState {
  if (state.status !== 'playing') {
    throw new Error('Game is not in playing state');
  }
  if (state.challenge.status === 'pending') {
    throw new Error('Cannot surrender with pending challenge, use respondChallenge instead');
  }

  const player = state.players.find((p) => p.id === playerId);
  if (!player || player.folded) {
    throw new Error(`Player ${playerId} is invalid or has already folded`);
  }

  if (state.mode === 'duel' || state.mode === 'teams') {
    const winner = state.players.find((p) => {
      if (p.folded || p.id === playerId) return false;
      if (state.mode === 'teams') return p.team !== player.team;
      return true;
    });
    if (!winner) return state;

    return awardBluffWin(
      {
        ...state,
        players: state.players.map((p) => (p.id === playerId ? { ...p, folded: true, hand: [] } : p)),
      },
      winner.id
    );
  } else {
    // FFA mode: mark folded
    const updatedPlayers = state.players.map((p) =>
      p.id === playerId ? { ...p, folded: true, hand: [] } : p
    );
    const active = updatedPlayers.filter((p) => !p.folded);
    if (active.length === 1) {
      return awardBluffWin(
        { ...state, players: updatedPlayers },
        active[0].id
      );
    }
    let nextTurn = state.currentTurnPlayerId;
    if (nextTurn === playerId) {
      nextTurn = getNextActivePlayerId(updatedPlayers, playerId);
    }
    return {
      ...state,
      players: updatedPlayers,
      currentTurnPlayerId: nextTurn,
      logs: [
        ...state.logs,
        {
          id: `log_${Date.now()}_fold`,
          timestamp: Date.now(),
          messageKey: 'event_player_folded',
          params: { name: player.name },
        },
      ],
    };
  }
}

export function playCard(state: GameState, playerId: string, cardId: string): GameState {
  if (state.status !== 'playing') {
    throw new Error('Game is not in playing state');
  }
  if (state.challenge.status === 'pending') {
    throw new Error('Cannot play card while a Bit-It challenge is pending response');
  }
  if (state.currentTurnPlayerId !== playerId) {
    throw new Error(`Not player ${playerId}'s turn (current turn: ${state.currentTurnPlayerId})`);
  }

  const player = state.players.find((p) => p.id === playerId);
  if (!player || player.folded) {
    throw new Error(`Player ${playerId} is invalid or has folded`);
  }

  const cardIndex = player.hand.findIndex((c) => c.id === cardId);
  if (cardIndex === -1) {
    throw new Error(`Card ${cardId} not found in player ${playerId}'s hand`);
  }

  const card = player.hand[cardIndex];
  const remainingHand = player.hand.filter((_, idx) => idx !== cardIndex);

  // Check Corven Slay condition
  // Corven slays Ezarion IF Ezarion was already played in this trick by an opponent/rival!
  let slayedEzarion = false;
  if (card.isCorven) {
    const ezarionPlay = state.currentTrick.plays.find((p) => p.card.isEzarion);
    if (ezarionPlay) {
      if (state.mode === 'teams') {
        const ezarionPlayer = state.players.find((p) => p.id === ezarionPlay.playerId);
        if (ezarionPlayer && ezarionPlayer.team !== player.team) {
          slayedEzarion = true;
        }
      } else {
        if (ezarionPlay.playerId !== playerId) {
          slayedEzarion = true;
        }
      }
    }
  }

  const playedCard: PlayedCard = {
    card,
    playerId,
    slayedEzarion,
  };

  const updatedCurrentTrick: Trick = {
    ...state.currentTrick,
    plays: [...state.currentTrick.plays, playedCard],
  };

  const updatedPlayers = state.players.map((p) => (p.id === playerId ? { ...p, hand: remainingHand } : p));

  // IF CORVEN SLAY OCCURRED: INSTANT ROUND WIN!
  if (slayedEzarion) {
    const winPoints = getStakesWinPoints(state.challenge.level, state.carriedOverBonus);
    const scoredPlayers = updatedPlayers.map((p) => {
      const isWinner = state.mode === 'teams' ? p.team === player.team : p.id === playerId;
      return isWinner ? { ...p, score: p.score + winPoints } : p;
    });

    const matchWinner = scoredPlayers.find((p) => p.score >= TARGET_SCORE);

    const resolvedSlayTrick: Trick = {
      ...updatedCurrentTrick,
      corvenSlayWinnerId: playerId,
      winnerId: playerId,
    };

    return {
      ...state,
      status: matchWinner ? 'match_end' : 'round_end',
      players: scoredPlayers,
      currentTrick: resolvedSlayTrick,
      completedTricks: [...state.completedTricks, resolvedSlayTrick],
      carriedOverBonus: 1,
      roundWinnerId: playerId,
      matchWinnerId: matchWinner ? (state.mode === 'teams' ? player.team! : matchWinner.id) : null,
      logs: [
        ...state.logs,
        {
          id: `log_${Date.now()}_slay`,
          timestamp: Date.now(),
          messageKey: 'event_corven_slay',
          params: { name: player.name },
        },
        {
          id: `log_${Date.now()}_win`,
          timestamp: Date.now(),
          messageKey: matchWinner ? 'event_match_won' : 'event_round_won',
          params: { name: player.name, points: winPoints },
        },
      ],
    };
  }

  // Check if trick is complete
  const activePlayers = updatedPlayers.filter((p) => !p.folded);
  const trickComplete = updatedCurrentTrick.plays.length === activePlayers.length;

  if (!trickComplete) {
    // Advance to next active player in clockwise order
    const nextPlayerId = getNextActivePlayerId(updatedPlayers, playerId);
    return {
      ...state,
      currentTurnPlayerId: nextPlayerId,
      players: updatedPlayers,
      currentTrick: updatedCurrentTrick,
    };
  }

  // Trick is complete! Evaluate trick winner
  const trickResolution = resolveTrick(updatedCurrentTrick, updatedPlayers, state.mode);
  const resolvedTrick: Trick = {
    ...updatedCurrentTrick,
    winnerId: trickResolution.winnerId,
    isTied: trickResolution.isTied,
  };

  const newCompletedTricks = [...state.completedTricks, resolvedTrick];

  // Log trick result
  let trickLogs = [...state.logs];
  if (resolvedTrick.isTied) {
    trickLogs.push({
      id: `log_${Date.now()}_tie`,
      timestamp: Date.now(),
      messageKey: 'event_trick_tied',
      params: { trick: resolvedTrick.trickNumber },
    });
  } else {
    const trickWinner = state.players.find((p) => p.id === resolvedTrick.winnerId)!;
    const winningPlay = resolvedTrick.plays.find((p) => p.playerId === resolvedTrick.winnerId)!;
    trickLogs.push({
      id: `log_${Date.now()}_trick`,
      timestamp: Date.now(),
      messageKey: 'event_trick_won',
      params: {
        trick: resolvedTrick.trickNumber,
        name: trickWinner.name,
        card: winningPlay.card.nameKey,
        power: winningPlay.card.power,
      },
    });
  }

  // Check if Round has a Winner
  const roundWinnerId = evaluateRoundWinner(state.mode, updatedPlayers, newCompletedTricks);

  if (roundWinnerId) {
    // Someone won the round!
    const winPoints = getStakesWinPoints(state.challenge.level, state.carriedOverBonus);
    const roundWinner = updatedPlayers.find((p) => p.id === roundWinnerId)!;
    const finalPlayers = updatedPlayers.map((p) => {
      const isWinner = state.mode === 'teams' ? p.team === roundWinner.team : p.id === roundWinnerId;
      return isWinner ? { ...p, score: p.score + winPoints } : p;
    });

    const matchWinner = finalPlayers.find((p) => p.score >= TARGET_SCORE);

    return {
      ...state,
      status: matchWinner ? 'match_end' : 'round_end',
      players: finalPlayers,
      currentTrick: resolvedTrick,
      completedTricks: newCompletedTricks,
      carriedOverBonus: 1,
      roundWinnerId,
      matchWinnerId: matchWinner ? (state.mode === 'teams' ? roundWinner.team! : matchWinner.id) : null,
      logs: [
        ...trickLogs,
        {
          id: `log_${Date.now()}_round`,
          timestamp: Date.now(),
          messageKey: matchWinner ? 'event_match_won' : 'event_round_won',
          params: { name: roundWinner.name, points: winPoints },
        },
      ],
    };
  }

  // If 3 tricks completed and still no round winner -> TRIPLE TIE!
  if (newCompletedTricks.length === 3) {
    return {
      ...state,
      status: 'round_end',
      players: updatedPlayers,
      currentTrick: resolvedTrick,
      completedTricks: newCompletedTricks,
      carriedOverBonus: state.carriedOverBonus * 2,
      roundWinnerId: null,
      matchWinnerId: null,
      logs: [
        ...trickLogs,
        {
          id: `log_${Date.now()}_triple_tie`,
          timestamp: Date.now(),
          messageKey: 'event_triple_tie',
        },
      ],
    };
  }

  // Otherwise, start next trick!
  // Trick leader is the winner of this trick, or if tied, the leader of the tied trick
  let nextLeaderId = resolvedTrick.winnerId || resolvedTrick.leaderId;
  const nextLeaderPlayer = updatedPlayers.find((p) => p.id === nextLeaderId);
  if (!nextLeaderPlayer || nextLeaderPlayer.folded) {
    nextLeaderId = getNextActivePlayerId(updatedPlayers, nextLeaderId);
  }

  const nextTrick: Trick = {
    trickNumber: resolvedTrick.trickNumber + 1,
    leaderId: nextLeaderId,
    plays: [],
    winnerId: null,
    isTied: false,
    corvenSlayWinnerId: null,
  };

  return {
    ...state,
    currentTurnPlayerId: nextLeaderId,
    players: updatedPlayers,
    currentTrick: nextTrick,
    completedTricks: newCompletedTricks,
    logs: trickLogs,
  };
}

export function resolveTrick(
  trick: Trick,
  players?: Player[],
  mode?: GameMode
): { winnerId: string | null; isTied: boolean } {
  if (trick.plays.length === 0) {
    return { winnerId: null, isTied: true };
  }

  let maxPower = -1;
  for (const play of trick.plays) {
    if (play.card.power > maxPower) {
      maxPower = play.card.power;
    }
  }

  const highestPlays = trick.plays.filter((p) => p.card.power === maxPower);
  if (highestPlays.length === 1) {
    return { winnerId: highestPlays[0].playerId, isTied: false };
  }

  // If there are multiple highest plays, check if in teams mode both belong to the same team!
  if (mode === 'teams' && players) {
    const teams = new Set(
      highestPlays.map((play) => players.find((p) => p.id === play.playerId)?.team)
    );
    // If all highest plays belong to the same team, that team won the trick!
    if (teams.size === 1 && !teams.has(undefined)) {
      return { winnerId: highestPlays[0].playerId, isTied: false };
    }
  }

  // Otherwise, opposing players/teams tied for highest power -> trick ties ("cravou")
  return { winnerId: null, isTied: true };
}

/**
 * ADR-001 Rule Implementation for Round Determination:
 * 1. Winning 2 tricks wins the round.
 * 2. 1-1-1 Tie in FFA: Winner of Trick 1 wins the round.
 * 3. Tie in Trick 2 and/or 3: Winner of Trick 1 wins the round.
 * 4. Tie in Trick 1:
 *    - Winner of Trick 2 wins the round.
 *    - If Trick 2 also ties, winner of Trick 3 wins.
 * 5. Triple Tie: All 3 tricks tie -> null (no winner, double next round).
 */
export function evaluateRoundWinner(
  mode: GameMode,
  players: Player[],
  tricks: Trick[]
): string | null {
  const trick1 = tricks[0];
  const trick2 = tricks[1];
  const trick3 = tricks[2];

  // Helper to count trick wins
  const countWins = (teamOrId: string, isTeam: boolean) => {
    return tricks.filter((t) => {
      if (!t.winnerId) return false;
      if (isTeam) {
        const winner = players.find((p) => p.id === t.winnerId);
        return winner?.team === teamOrId;
      }
      return t.winnerId === teamOrId;
    }).length;
  };

  if (mode === 'teams') {
    const winsA = countWins('A', true);
    const winsB = countWins('B', true);

    if (winsA >= 2) return players.find((p) => p.team === 'A')!.id;
    if (winsB >= 2) return players.find((p) => p.team === 'B')!.id;

    // If trick 1 tied, whoever wins trick 2 wins
    if (trick1?.isTied && trick2?.winnerId) {
      return trick2.winnerId;
    }
    // If trick 1 & 2 tied, whoever wins trick 3 wins
    if (trick1?.isTied && trick2?.isTied && trick3?.winnerId) {
      return trick3.winnerId;
    }
    // If trick 2 or 3 tied, winner of trick 1 wins
    if (trick1?.winnerId && (trick2?.isTied || trick3?.isTied)) {
      return trick1.winnerId;
    }
  } else {
    // Duel or FFA
    // 1. Check if any player won 2 tricks
    for (const p of players) {
      if (countWins(p.id, false) >= 2) {
        return p.id;
      }
    }

    // 2. If Trick 1 tied:
    if (trick1?.isTied) {
      if (trick2?.winnerId) return trick2.winnerId;
      if (trick2?.isTied && trick3?.winnerId) return trick3.winnerId;
    }

    // 3. If Trick 1 had a winner:
    if (trick1?.winnerId) {
      // If Trick 2 or Trick 3 tied, Trick 1 winner takes the round!
      if (trick2?.isTied || trick3?.isTied) {
        return trick1.winnerId;
      }
      // 4. In FFA 1-1-1 tie (3 different winners across 3 tricks):
      if (tricks.length === 3 && trick1.winnerId && trick2?.winnerId && trick3?.winnerId) {
        if (trick1.winnerId !== trick2.winnerId && trick2.winnerId !== trick3.winnerId && trick1.winnerId !== trick3.winnerId) {
          return trick1.winnerId; // Winner of 1st trick wins FFA 1-1-1
        }
      }
    }
  }

  return null;
}

export function getNextActivePlayerId(players: Player[], currentId: string): string {
  const currentIndex = players.findIndex((p) => p.id === currentId);
  const total = players.length;
  for (let step = 1; step <= total; step++) {
    const idx = (currentIndex + step) % total;
    if (!players[idx].folded) {
      return players[idx].id;
    }
  }
  return currentId;
}

export function getStakesWinPoints(level: StakesLevel, bonus: number = 1): number {
  switch (level) {
    case 'normal':
      return 2 * bonus;
    case 'bit_it':
      return 4 * bonus;
    case 'double_bit_it':
      return 8 * bonus;
  }
}

export function getStakesFoldPoints(
  level: StakesLevel,
  bonus: number = 1,
  isPendingChallenge: boolean = true
): number {
  if (isPendingChallenge) {
    switch (level) {
      case 'normal':
        return 1 * bonus;
      case 'bit_it':
        // Opponent folded to Bit-It request without accepting: caller wins previous phase fold points = 1 pt
        return 1 * bonus;
      case 'double_bit_it':
        // Opponent folded to Double Bit-It request without accepting: caller wins bit-it phase fold points = 2 pts
        return 2 * bonus;
    }
  } else {
    // Challenge was already accepted (or normal round): folding now forfeits the current phase fold value!
    switch (level) {
      case 'normal':
        return 1 * bonus;
      case 'bit_it':
        // Bit-It was accepted (playing for 2-4): folding now awards 2 points to winner!
        return 2 * bonus;
      case 'double_bit_it':
        // Double Bit-It was accepted (playing for 4-8): folding now awards 4 points to winner!
        return 4 * bonus;
    }
  }
}

/**
 * Calculates active binary bits for a given score (up to 15).
 * Bit 0: 1 pt
 * Bit 1: 2 pts
 * Bit 2: 4 pts
 * Bit 3: 8 pts
 */
export function getBinaryBits(score: number): [boolean, boolean, boolean, boolean] {
  const clamped = Math.max(0, Math.min(15, score));
  return [
    (clamped & 1) !== 0,
    (clamped & 2) !== 0,
    (clamped & 4) !== 0,
    (clamped & 8) !== 0,
  ];
}
